"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { Calendar, ChevronDown, Coins, Crown, FileText, Headphones } from "lucide-react";
import { cn, formatRelativeTime } from "@/lib/utils";
import { ACCESS_TIER } from "@/lib/db/schema";
import { isChapterPaidAt } from "@/lib/paywall/scheduled-access";
import { StoryReaderLink } from "@/components/reader/story-reader-link";
import { VolumeContentsControls } from "@/components/reader/volume-contents-controls";
import { useVolumeChapterGroups } from "@/components/reader/use-volume-chapter-groups";
interface ChapterListItem {
  id: string;
  title: string;
  sortOrder: number;
  wordCount: number | null;
  publishedAt: Date | null;
  accessTier: string;
  freeAfter?: Date | null;
  hasAudio: boolean;
  volumeId: string;
  volumeTitle: string;
}
interface ChapterListProps {
  chapters: ChapterListItem[];
  readerHrefBase: string;
  savedChapterId: string | null;
  showTextToSpeech?: boolean;
}
interface ChapterRowsProps {
  chapters: ChapterListItem[];
  readerHrefBase: string;
  savedChapterId: string | null;
  showTextToSpeech?: boolean;
}
function ChapterRows(t0) {
  const $ = _c(16);
  const {
    chapters,
    readerHrefBase,
    savedChapterId,
    showTextToSpeech: t1
  } = t0;
  const showTextToSpeech = t1 === undefined ? true : t1;
  const [now, setNow] = useState(null);
  let t2;
  let t3;
  if ($[0] !== chapters) {
    t2 = () => {
      let timer;
      const tick = () => {
        const instant = new Date();
        setNow(instant);
        const next = Math.min(...chapters.map(_temp).filter(time => time > instant.getTime()));
        if (Number.isFinite(next)) {
          timer = setTimeout(tick, Math.min(next - instant.getTime(), 2147000000));
        }
      };
      timer = setTimeout(tick, 0);
      return () => clearTimeout(timer);
    };
    t3 = [chapters];
    $[0] = chapters;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] !== chapters || $[4] !== now || $[5] !== readerHrefBase || $[6] !== savedChapterId || $[7] !== showTextToSpeech) {
    let t5;
    if ($[9] !== now || $[10] !== readerHrefBase || $[11] !== savedChapterId || $[12] !== showTextToSpeech) {
      t5 = chapter_0 => {
        const isPremium = chapter_0.accessTier === ACCESS_TIER.PREMIUM && (now === null || isChapterPaidAt(ACCESS_TIER.PREMIUM, chapter_0.freeAfter ? new Date(chapter_0.freeAfter) : null, now));
        const isBasic = chapter_0.accessTier === ACCESS_TIER.BASIC;
        const isCurrent = chapter_0.id === savedChapterId;
        return <li key={chapter_0.id}><StoryReaderLink readerHrefBase={readerHrefBase} chapterId={chapter_0.id} className={cn("flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-secondary/40 md:px-5", isCurrent && "bg-brand/5")}><div className="min-w-0 flex-1"><div className="flex flex-wrap items-center gap-2"><span className="truncate font-medium text-foreground">{chapter_0.title}</span>{isCurrent && <span className="inline-flex items-center gap-1 rounded bg-brand/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-light">Reading</span>}</div><div className="mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">{chapter_0.publishedAt && <span className="inline-flex items-center gap-1"><Calendar className="size-3" />{formatRelativeTime(chapter_0.publishedAt)}</span>}{showTextToSpeech && chapter_0.hasAudio && <span className="inline-flex items-center gap-1" title="Audio narration available"><Headphones className="size-3" /></span>}</div></div><div className="flex flex-none items-center gap-2">{isPremium ? <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/15 px-2 py-1 text-[11px] font-semibold text-amber-300"><Coins className="size-3" /> Premium</span> : isBasic ? <span className="inline-flex items-center gap-1 rounded-full border border-brand/30 bg-brand/15 px-2 py-1 text-[11px] font-semibold text-brand-light"><Crown className="size-3" /> Basic</span> : <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-1 text-[11px] font-semibold text-emerald-300">Free</span>}</div></StoryReaderLink></li>;
      };
      $[9] = now;
      $[10] = readerHrefBase;
      $[11] = savedChapterId;
      $[12] = showTextToSpeech;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    t4 = chapters.map(t5);
    $[3] = chapters;
    $[4] = now;
    $[5] = readerHrefBase;
    $[6] = savedChapterId;
    $[7] = showTextToSpeech;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[14] !== t4) {
    t5 = <ul className="divide-y divide-border">{t4}</ul>;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
function _temp(chapter) {
  return chapter.freeAfter ? new Date(chapter.freeAfter).getTime() : Infinity;
}
export function ChapterList(t0) {
  const $ = _c(30);
  const {
    chapters,
    readerHrefBase,
    savedChapterId,
    showTextToSpeech: t1
  } = t0;
  const showTextToSpeech = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== chapters || $[1] !== savedChapterId) {
    t2 = chapters.find(chapter => chapter.id === savedChapterId)?.volumeId;
    $[0] = chapters;
    $[1] = savedChapterId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const savedVolumeId = t2;
  const firstVolumeId = chapters.reduce(_temp2, undefined)?.volumeId;
  const initialVolumeId = savedVolumeId ?? firstVolumeId;
  const {
    areAllExpanded,
    direction,
    expandedVolumeIds,
    groups: volumeGroups,
    hasMultipleVolumes,
    toggleAllVolumes,
    toggleDirection,
    toggleVolume
  } = useVolumeChapterGroups(chapters, initialVolumeId);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h2 className="font-heading text-2xl font-bold text-landing-heading">Contents</h2>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = chapters.length === 1 ? "chapter" : "chapters";
  const t5 = hasMultipleVolumes && ` · ${volumeGroups.length} volumes`;
  let t6;
  if ($[4] !== chapters.length || $[5] !== t4 || $[6] !== t5) {
    t6 = <div>{t3}<p className="mt-1 text-sm text-muted-foreground">{chapters.length} {t4}{t5}</p></div>;
    $[4] = chapters.length;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== areAllExpanded || $[9] !== chapters.length || $[10] !== direction || $[11] !== hasMultipleVolumes || $[12] !== toggleAllVolumes || $[13] !== toggleDirection) {
    t7 = chapters.length > 1 && <VolumeContentsControls direction={direction} areAllExpanded={areAllExpanded} onToggleDirection={toggleDirection} onToggleAll={toggleAllVolumes} showVolumeToggle={hasMultipleVolumes} />;
    $[8] = areAllExpanded;
    $[9] = chapters.length;
    $[10] = direction;
    $[11] = hasMultipleVolumes;
    $[12] = toggleAllVolumes;
    $[13] = toggleDirection;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <div className="mb-4 flex flex-wrap items-end justify-between gap-3">{t6}{t7}</div>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== chapters.length || $[19] !== expandedVolumeIds || $[20] !== hasMultipleVolumes || $[21] !== readerHrefBase || $[22] !== savedChapterId || $[23] !== showTextToSpeech || $[24] !== toggleVolume || $[25] !== volumeGroups) {
    t9 = chapters.length === 0 ? <div className="rounded-2xl border border-border bg-card/40 p-10 text-center"><FileText className="mx-auto mb-3 size-10 text-muted-foreground/40" /><p className="text-sm text-muted-foreground">No chapters have been published yet.</p></div> : hasMultipleVolumes ? <div className="overflow-hidden rounded-2xl border border-border bg-card/40">{volumeGroups.map(group => {
        const isExpanded = expandedVolumeIds.has(group.id);
        const hasCurrentChapter = group.chapters.some(chapter_1 => chapter_1.id === savedChapterId);
        return <section key={group.id} data-testid="volume-section" className="border-t border-border first:border-t-0"><h3><button type="button" onClick={() => toggleVolume(group.id)} aria-expanded={isExpanded} aria-controls={`volume-panel-${group.id}`} className="flex w-full items-center gap-3 px-4 py-4 text-left transition-colors hover:bg-secondary/40 md:px-5"><ChevronDown className={cn("size-4 flex-none text-muted-foreground transition-transform", !isExpanded && "-rotate-90")} /><span className="min-w-0 flex-1 truncate font-heading font-bold text-foreground">{group.title}</span>{hasCurrentChapter && <span className="rounded-full bg-brand/15 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-brand-light">Reading</span>}</button></h3>{isExpanded && <div id={`volume-panel-${group.id}`}><ChapterRows chapters={group.chapters} readerHrefBase={readerHrefBase} savedChapterId={savedChapterId} showTextToSpeech={showTextToSpeech} /></div>}</section>;
      })}</div> : <div className="overflow-hidden rounded-2xl border border-border bg-card/40"><ChapterRows chapters={volumeGroups[0]?.chapters ?? []} readerHrefBase={readerHrefBase} savedChapterId={savedChapterId} showTextToSpeech={showTextToSpeech} /></div>;
    $[18] = chapters.length;
    $[19] = expandedVolumeIds;
    $[20] = hasMultipleVolumes;
    $[21] = readerHrefBase;
    $[22] = savedChapterId;
    $[23] = showTextToSpeech;
    $[24] = toggleVolume;
    $[25] = volumeGroups;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t8 || $[28] !== t9) {
    t10 = <section className="mt-12 md:mt-16">{t8}{t9}</section>;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}
function _temp2(first, chapter_0) {
  return !first || chapter_0.sortOrder < first.sortOrder ? chapter_0 : first;
}